import { AdvertiserNode, Aggregate, CampaignNode, LineItem, PacingStatus } from "../types/pacing.types";


const toNum = (v: any) => (v == null || v === "" ? 0 : Number(v));

export function to2dp(raw: string) {
  // keep digits + one dot
  let v = raw.replace(/[^\d.]/g, "");
  const parts = v.split(".");
  v = parts[0] + (parts[1] !== undefined ? "." + parts[1].slice(0, 2) : "");
  // avoid lone "."
  if (v === ".") v = "0.";
  return v;
}

export const setLineItemUpdateData = (lineItem: LineItem, field: string, value: any) => {
  if (field === 'start_date' || field === 'end_date' || field === 'daily_budget' || field === 'budget') {
    const dailyBudget = field === 'daily_budget' ? value : toNum(lineItem.daily_budget);
    const budget = field === 'budget' ? value : toNum(lineItem.budget);
    let daysRemaining = lineItem.days_remaining;

    if (field === 'start_date') {
      const startDate = new Date(value);
      const endDate = new Date(lineItem.end_date);
      daysRemaining = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return {
        [field]: value,
        pacing_item_status: computeStatus(daysRemaining, dailyBudget, toNum(lineItem.spend), budget),
        days_remaining: daysRemaining,
      };
    }

    if (field === 'end_date') {
      const startDate = new Date(lineItem.start_date);
      const endDate = new Date(value);
      daysRemaining = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return {
        [field]: value,
        pacing_item_status: computeStatus(daysRemaining, dailyBudget, toNum(lineItem.spend), budget),
        days_remaining: daysRemaining,
      };
    }

    return {
      [field]: value,
      pacing_item_status: computeStatus(daysRemaining, dailyBudget, toNum(lineItem.spend), budget),
    };
  }
  return { [field]: value };
};

export const computeStatus = (daysRemaining: number, dailyBudget: number, spend: number, budget: number, band: number = 0.05): PacingStatus => {
  const spendAtPace = dailyBudget * daysRemaining;
  const remainingBudget = budget - spend;

  if (!isFinite(spendAtPace) || !isFinite(remainingBudget)) return "not_set";

  const upper = spendAtPace * (1 + band);
  const lower = spendAtPace * (1 - band);

  if (remainingBudget >= lower && remainingBudget <= upper) return "on_track";
  if (remainingBudget < upper) return "ahead";
  if (remainingBudget > lower) return "behind";

  return "not_set";
};

export const computePercentComplete = (budget: number, spend: number): number => {
  return budget > 0 ? (spend / budget) * 100 : 0;
};


export function recomputeCampaignAgg(c: CampaignNode): Aggregate {
  const budget = c.line_items.reduce((s, li) => s + toNum(li.budget), 0);
  const spend = c.line_items.reduce((s, li) => s + toNum(li.spend), 0);
  const daily = c.line_items.reduce((s, li) => s + toNum(li.daily_budget), 0);
  const impressions = c.line_items.reduce((s, li) => s + (li.impressions ?? 0), 0);
  const is_visible = c.line_items.some((li) => li.is_visible);
  const is_locked = c.line_items.some((li) => li.is_locked);

  const starts = c.line_items.map(li => li.start_date).filter(Boolean).sort();
  const ends = c.line_items.map(li => li.end_date).filter(Boolean).sort();

  const order = { behind: 3, ahead: 2, on_track: 1, not_set: 0, update: 10 } as const;
  const status = c.line_items.reduce<keyof typeof order>(
    (cur, li) => {
      const s = (li.pacing_item_status ?? "not_set") as keyof typeof order;
      return order[s] > order[cur] ? s : cur;
    },
    "not_set"
  );

  return {
    budget,
    spend,
    daily_budget: daily,
    impressions,
    start_date: (starts[0]) ?? null,
    end_date: (ends[ends.length - 1]) ?? null,
    percent_complete: budget > 0 ? (spend / budget) * 100 : null,
    status,
    is_visible,
    is_locked,
  };
}

export function recomputeAdvertiserAgg(a: AdvertiserNode): Aggregate {
  const order = { behind: 3, ahead: 2, on_track: 1, not_set: 0, update: 10 } as const;
  const agg = a.campaigns.reduce<Aggregate>(
    (tot, c) => {
      const startOpts = [tot.start_date, c.agg.start_date].filter(Boolean).sort() as `${number}-${number}-${number}`[];
      const endOpts = [tot.end_date, c.agg.end_date ].filter(Boolean).sort() as `${number}-${number}-${number}`[];
      const status = order[c.agg.status] > order[tot.status] ? c.agg.status : tot.status;
      return {
        budget: tot.budget + c.agg.budget,
        spend:  tot.spend + c.agg.spend,
        daily_budget: tot.daily_budget + c.agg.daily_budget,
        impressions: tot.impressions + c.agg.impressions,
        start_date: startOpts[0] ?? null,
        end_date: endOpts[endOpts.length - 1] ?? null,
        percent_complete: null,
        status,
        is_visible: tot.is_visible || c.line_items.some((li) => li.is_visible),
        is_locked: tot.is_locked || c.line_items.some((li) => li.is_locked),
      };
    },
    { budget: 0, spend: 0, daily_budget: 0, impressions: 0, start_date: null, end_date: null, percent_complete: null, status: "not_set", is_visible: false, is_locked: false }
  );

  agg.percent_complete = agg.budget > 0 ? (agg.spend / agg.budget) * 100 : null;
  return agg;
}

/**
 * Patch one field on a line by id, recompute campaign and advertiser,
 * and crucially *replace* the campaign & advertiser objects to update references.
 * Returns true if a line was found.
 */
export function patchLineAndRecompute(
  draft: AdvertiserNode[],
  lineId: string,
  field: keyof LineItem,
  value: any
): boolean {
  for (let ai = 0; ai < draft.length; ai++) {
    const adv = draft[ai];
    for (let ci = 0; ci < adv.campaigns.length; ci++) {
      const camp = adv.campaigns[ci];
      const li = camp.line_items.find(l => l.id === lineId);
      let daysRemaining = li?.days_remaining;

      if (!li) continue;

      // patch the line
      li[field] = value as never;

      if (field === 'end_date') {
        daysRemaining = Math.ceil((new Date(value).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) as number;
        li.days_remaining = daysRemaining;
        li.pacing_item_status = 'update';
      }

      if (field === 'start_date') {
        const startDate = new Date(value);
        if (startDate < new Date()) {
          daysRemaining = Math.ceil((new Date(li.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) as number;
          li.days_remaining = daysRemaining;
        }
        li.pacing_item_status = 'update';
      }

      if (field !== 'start_date' && field !== 'end_date') {
        li.pacing_item_status = computeStatus(daysRemaining ?? 0, toNum(li.daily_budget), toNum(li.spend), toNum(li.budget));
      }
      li.percent_complete = toNum(computePercentComplete(toNum(li.budget), li.spend).toFixed(0));

      // recompute aggregates
      const newCampAgg = recomputeCampaignAgg(camp);
      const newCamp: CampaignNode = { ...camp, agg: newCampAgg }; // new reference
      adv.campaigns[ci] = newCamp; // replace in array

      const newAdvAgg = recomputeAdvertiserAgg({ ...adv, campaigns: adv.campaigns });
      const newAdv: AdvertiserNode = { ...adv, agg: newAdvAgg }; // new reference
      draft[ai] = newAdv;

      return true;
    }
  }
  return false;
}

/**
 * Replace all lines for a campaign, then recompute and replace objects.
 */
export function replaceCampaignLines(
  draft: AdvertiserNode[],
  campaignId: string,
  newLines: LineItem[]
): boolean {
  for (let ai = 0; ai < draft.length; ai++) {
    const adv = draft[ai];
    const ci = adv.campaigns.findIndex(c => c.id === campaignId);
    if (ci === -1) continue;

    const oldCamp = adv.campaigns[ci];
    const camp: CampaignNode = { ...oldCamp, line_items: newLines.slice() }; // new reference
    camp.agg = recomputeCampaignAgg(camp);
    adv.campaigns[ci] = camp;

    const newAdv: AdvertiserNode = { ...adv, agg: recomputeAdvertiserAgg(adv) };
    draft[ai] = newAdv;
    return true;
  }
  return false;
}
